import { useQuery } from '@tanstack/react-query'
import { BarChart3, Phone, Trophy } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import { TierDistribution } from '../components/TierDistribution'
import type { Tier } from '../types'

export function HomePage() {
  const stats = useQuery({ queryKey: ['stats'], queryFn: () => api.stats() })
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health() })
  const agents = useQuery({ queryKey: ['agents'], queryFn: () => api.agents.list() })
  const recent = useQuery({
    queryKey: ['calls', 'recent'],
    queryFn: () => api.calls.list({ limit: 5, offset: 0, sort: '-call_started_at' }),
  })

  const tierCounts: Record<Tier, number> = { 1: 0, 2: 0, 3: 0, 4: 0 }
  if (agents.data) {
    for (const a of agents.data) {
      if (a.tier === 1 || a.tier === 2 || a.tier === 3 || a.tier === 4) {
        tierCounts[a.tier] += 1
      }
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">killacoach</h1>
        <p className="mt-1 text-sm text-slate-400">
          Call-coaching rubric scores across VICIdial agents.
        </p>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Calls" value={stats.data?.calls_total ?? '...'} />
        <Stat label="Scored" value={stats.data?.scored ?? '...'} />
        <Stat
          label="Queue"
          value={stats.data?.queue_depth ?? '...'}
          sub={stats.data?.queue_depth ? 'rescores pending' : 'idle'}
        />
        <Stat
          label="Last updated"
          value={stats.data ? formatRelative(stats.data.last_run) : '...'}
          sub={
            stats.data?.last_run
              ? new Date(stats.data.last_run).toLocaleString()
              : health.data?.version
                ? `v${health.data.version}`
                : undefined
          }
        />
      </section>

      <section>
        <TierDistribution counts={tierCounts} />
      </section>

      <section className="grid gap-3 md:grid-cols-3">
        <NavCard
          to="/leaderboard"
          icon={<Trophy className="h-5 w-5 text-amber-400" />}
          title="Leaderboard"
          desc="Rank agents by average overall score and per-metric pass rate."
        />
        <NavCard
          to="/calls"
          icon={<Phone className="h-5 w-5 text-indigo-400" />}
          title="Calls"
          desc="Filter, review and drill into individual scored calls."
        />
        <NavCard
          to="/agents"
          icon={<BarChart3 className="h-5 w-5 text-emerald-400" />}
          title="Agents"
          desc="Per-agent summary, score distribution, dispo breakdown."
        />
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-baseline justify-between">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">
            Recent activity
          </p>
          <Link to="/calls" className="text-xs text-slate-400 hover:text-slate-200">
            View all →
          </Link>
        </div>

        {recent.isLoading ? (
          <p className="mt-2 text-xs text-slate-600">Loading...</p>
        ) : recent.data && recent.data.items.length > 0 ? (
          <ul className="mt-2 divide-y divide-slate-800">
            {recent.data.items.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/calls/${c.id}`}
                  className="flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:bg-slate-900/60"
                >
                  <span className="min-w-0 flex-1 truncate text-slate-200">
                    {c.agent_name || c.vici_user}
                  </span>
                  <span
                    className={`font-mono text-xs ${
                      c.overall === null
                        ? 'text-slate-600'
                        : c.overall >= 80
                          ? 'text-emerald-300'
                          : c.overall >= 60
                            ? 'text-amber-300'
                            : 'text-red-300'
                    }`}
                  >
                    {c.overall === null ? '--' : c.overall.toFixed(0)}
                  </span>
                  <span className="w-24 text-right text-xs text-slate-500">
                    {formatRelative(c.call_started_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-slate-600">No calls scored yet.</p>
        )}
      </section>

      {stats.data && stats.data.calls_total === 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-center">
          <p className="text-sm text-slate-400">
            No calls yet. Run{' '}
            <code className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-xs text-slate-200">
              python -m killacoach.runner.cli --agent 1201 --limit 50
            </code>
            .
          </p>
        </div>
      )}
    </div>
  )
}

// Relative-time helper. Uses Intl.RelativeTimeFormat (built-in, no dep).
// Returns "never run" for null/undefined, "just now" for <60s.
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never run'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '--'
  const diffSec = Math.round((then - Date.now()) / 1000)
  const absSec = Math.abs(diffSec)

  if (absSec < 60) return 'just now'

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const units: Array<{ unit: Intl.RelativeTimeFormatUnit; sec: number }> = [
    { unit: 'year', sec: 60 * 60 * 24 * 365 },
    { unit: 'month', sec: 60 * 60 * 24 * 30 },
    { unit: 'week', sec: 60 * 60 * 24 * 7 },
    { unit: 'day', sec: 60 * 60 * 24 },
    { unit: 'hour', sec: 60 * 60 },
    { unit: 'minute', sec: 60 },
  ]
  for (const { unit, sec } of units) {
    if (absSec >= sec) {
      return rtf.format(Math.round(diffSec / sec), unit)
    }
  }
  return rtf.format(diffSec, 'second')
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string
  value: string | number
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-100">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  )
}

function NavCard({
  to,
  icon,
  title,
  desc,
}: {
  to: string
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <Link
      to={to}
      className="block rounded-lg border border-slate-800 bg-slate-900/40 p-4 transition-colors hover:border-slate-700 hover:bg-slate-900"
    >
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="font-medium text-slate-100">{title}</h3>
      </div>
      <p className="mt-2 text-xs text-slate-400">{desc}</p>
    </Link>
  )
}
